import { useUserLoggedIn } from '~/modules/user/hook/use-user';
import { useSetLoginModal } from '~/modules-rocco/user/hook/use-open-login-modal';
import { useHeroMarket } from '~/ui-rocco/pages/home-page/sections/hero-markets';

/**
 * Interspersed sign-in prompt for the directory browse pages. Shown to
 * logged-out users only; renders nothing once signed in. Sign in / Sign up
 * open the login/sign-up modal in place (the form carries both flows), so the
 * user never leaves the grid they were browsing.
 *
 * Rendered as an editorial image banner — the market hero photo under the
 * detail-closing gradient treatment (HeroSection variant="detail") — so the
 * injection reads as a designed break in the grid rather than a bare bordered
 * box. The photo/gradient carry their own contrast, so the block is
 * theme-independent (no light/dark variants).
 */
export const DirectoryPromoBanner = () => {
    const { isLoggedIn } = useUserLoggedIn();
    const { setLoginModal } = useSetLoginModal();
    const { image, mobileImage } = useHeroMarket();

    if (isLoggedIn) {
        return null;
    }

    const openLoginModal = () =>
        setLoginModal({ authSource: 'directoryPromoBanner', isOpen: true });

    return (
        <div className="relative overflow-hidden rounded-tile">
            <picture>
                <source media="(min-width: 768px)" srcSet={image} />
                <img
                    src={mobileImage}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    className="absolute inset-0 size-full object-cover"
                />
            </picture>
            {/* Bottom gradient for legibility — same recipe as the homepage /
                detail-closing hero. */}
            <div
                aria-hidden="true"
                className="absolute inset-x-0 bottom-0 h-3/4 bg-linear-to-t from-[#131313] from-[6.207%] to-transparent to-[70.169%] opacity-[0.89]"
            />
            <div className="relative flex min-h-[18rem] flex-col justify-end gap-5 p-6 md:min-h-[26rem] md:p-10">
                <div className="flex flex-col gap-2">
                    <h3 className="m-0 text-[1.75rem] leading-[1.15] font-normal tracking-[-0.02rem] text-[#e5e5e5] md:text-[2.5rem]">
                        Unlock all features
                    </h3>
                    <p className="m-0 max-w-[36rem] text-sm text-[#fafafa]/90 md:text-base">
                        Access the complete information of the catalogue most
                        used by Architects and Interior Designers
                    </p>
                </div>
                <div className="flex items-center gap-4">
                    <button
                        type="button"
                        onClick={openLoginModal}
                        className="flex items-center rounded-full bg-[#f2f2f2] px-7 py-2.5 text-sm font-medium text-[#323232] transition-colors hover:bg-offwhite max-md:min-h-11"
                    >
                        Sign in
                    </button>
                    <button
                        type="button"
                        onClick={openLoginModal}
                        className="flex items-center text-sm font-medium text-offwhite underline max-md:min-h-11 max-md:px-2"
                    >
                        Sign up
                    </button>
                </div>
            </div>
        </div>
    );
};
